import React, { useState, useEffect } from 'react';
import { Navigate, NavLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Tooltip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Unarchive as UnarchiveIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Archive as ArchiveIcon,
  Place as SiteIcon,
} from "@mui/icons-material";
import AddSite from './AddSite';
import ViewRequest from './ViewRequest';
import EditSite from './EditSite';
import config from '../../../../../config/config.json';
import { apiCall } from '../../../../../services/ApiCall';

const Main = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Original state variables (unchanged)
  const [page, setPage] = useState('main');
  const [selectedSite, setSelectedSite] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(true);

  const primaryColor = "#0d6efd";

  // Original event handlers (unchanged logic)
  const handleAddSite = () => {
    setPage('addSite');
  };

  const handleSaveEdit = (updatedSite) => {
    const updatedData = data.map((site) =>
      site.id === updatedSite.araId ? updatedSite : site
    );
    setData(updatedData);
    setPage('main');
  };

  const handleCancelEdit = () => {
    setPage('main');
  };

  const handleActionIconClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleAction = async () => {
    try {
      await actionSite(deleteId);
      setShowDeleteModal(false);
      setSnackbar({ open: true, message: "Site unarchived successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to unarchive site", severity: "error" });
    }
  };

  // Original sorting logic (unchanged)
  const sortData = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    const sortedData = [...data].sort((a, b) => {
      if (key === '#') {
        return direction === 'ascending' ? a.sequence - b.sequence : b.sequence - a.sequence;
      } else {
        if (a[key] < b[key]) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      }
    });

    setSortConfig({ key, direction });
    setData(sortedData);
  };

  // Original API functions (unchanged logic)
  const actionSite = async (id) => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}unArchiveSite`,
      {},
      { siteId: id },
      "POST"
    );
    if (isSuccess) {
      getArchiveSitesList();
    }
  };

  const getArchiveSitesList = async () => {
    setLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getArchiveSitesList`,
        {},
        {},
        "GET"
      );
      if (isSuccess) {
        setData(data?.data?.reverse() || []);
      }
    } catch (error) {
      console.error("Error fetching archived sites:", error);
      setSnackbar({ open: true, message: "Failed to load archived sites", severity: "error" });
    }
    setLoading(false);
  };

  // Original data processing (unchanged logic)
  const numberedData = data.map((row, index) => ({ ...row, sequence: index + 1 }));

  const filteredData = numberedData.filter(row => {
    return ['id', 'araId', 'company', 'name', 'street', 'city', 'state', 'zipcode', 'address'].some(key =>
      row[key]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Original useEffect (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getArchiveSitesList();
  }, []);

  // DataGrid columns configuration
  const columns = [
    {
      field: 'id',
      headerName: '#',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'araId',
      headerName: 'ARA ID',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ color: primaryColor, fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      width: 120,
    },
    {
      field: 'state',
      headerName: 'State',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          sx={{ 
            backgroundColor: `${primaryColor}15`,
            color: primaryColor,
            fontWeight: 500
          }} 
        />
      ),
    },
    {
      field: 'zipcode',
      headerName: 'Zipcode',
      width: 100,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
          <Tooltip title="View Site Details">
            <IconButton
              component={NavLink}
              to={`/view-site`}
              size="small"
              sx={{ color: primaryColor }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Unarchive Site">
            <IconButton
              size="small"
              onClick={() => handleActionIconClick(params.row.id)}
              sx={{ color: theme.palette.success.main }}
            >
              <UnarchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // Original conditional rendering (unchanged logic)
  if (page === 'addSite') {
    return <AddSite />;
  } else if (page === 'viewRequest') {
    return <ViewRequest />;
  } else if (page === 'editSite') {
    return <EditSite siteData={selectedSite} onSave={handleSaveEdit} onCancel={handleCancelEdit} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ArchiveIcon sx={{ fontSize: 36 }} />
                Archived Sites
              </Box>
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and restore archived site records
            </Typography>
          </Box>
        </Box>

        {/* Controls Row */}
        <Grid container spacing={2} alignItems="center">
          {/* Show Entries */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Show
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={paginationModel.pageSize}
                  onChange={(e) => setPaginationModel({
                    ...paginationModel,
                    pageSize: Number(e.target.value),
                    page: 0
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={250}>250</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                entries
              </Typography>
            </Box>
          </Grid>

          {/* Search Box */}
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by ID, ARA ID, Company, Address, City, State..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          
          {/* Results Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Typography variant="body2" color="text.secondary">
                {filteredData.length} archived sites
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper elevation={1} sx={{ height: 600, borderRadius: 2 }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100, 250, 500]}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${primaryColor}08`,
              borderBottom: `2px solid ${primaryColor}20`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              color: primaryColor,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: `${primaryColor}05`,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Paper>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Confirm Unarchive</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unarchive this site? This will restore it to the active sites list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button 
            onClick={handleAction} 
            color="success"
            variant="contained"
            startIcon={<UnarchiveIcon />}
          >
            Yes, Unarchive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Main;